/* storybook-check-ignore */
import Head from 'next/head';

import {
  IArticleFields,
  IAuthor,
  IAuthorFields,
  IOpenGraphArticleFields,
  IOpenGraphFields,
  IPublisherFields,
  ISeoBaseFields,
  ITwitterCardFields,
} from '../../../declarations/contentful';
import { getSocialLink } from '../../../helpers/socialLinks';

const getBaseTags = (props: { base?: ISeoBaseFields }) => {
  if (!props.base || !props.base.fields) {
    return null;
  }
  const { canonicalUrl, metaDescription, title } = props.base.fields;
  return [
    <title key="title">{title}</title>,
    <meta key="description" name="description" content={metaDescription} />,
    <link key="canonical" rel="canonical" href={canonicalUrl} />,
  ];
};

const getOgTags = (props: {
  og?: IOpenGraphFields;
  ogArticle?: IOpenGraphArticleFields;
  authors?: IAuthor[];
  createdAt: string;
  updatedAt: string;
}) => {
  if (!props.og || !props.ogArticle || !props.og.fields || !props.ogArticle.fields) {
    return null;
  }
  const { url, type, title, description, image, siteName } = props.og.fields;
  // TODO: may need to double check what time format these are in
  const {
    // fallback to article created at and updated at if not explicitly
    // provided
    publishedTime = props.createdAt,
    modifiedTime = props.updatedAt,
    expirationDate,
    section,
    tags,
  } = props.ogArticle.fields;
  return [
    <meta key="og:title" property="og:title" content={title} />,
    <meta key="og:type" property="og:type" content={type} />,
    <meta key="og:url" property="og:url" content={url} />,
    <meta key="og:image" property="og:image" content={image} />,
    <meta key="og:description" property="og:description" content={description} />,
    <meta key="og:site_name" property="og:site_name" content={siteName} />,
    <meta
      key="og:article:published_time"
      property="og:article:published_time"
      content={publishedTime}
    />,
    <meta
      key="og:article:modified_time"
      property="og:article:modified_time"
      content={modifiedTime}
    />,
    <meta key="og:article:section" property="og:article:section" content={section} />,
    expirationDate && (
      <meta
        key="og:article:expiration_time"
        property="og:article:expiration_time"
        content={expirationDate}
      />
    ),
    (props.authors || []).map((author) => (
      <meta
        key={author.fields.displayName}
        property="og:article:author"
        content={author.fields.displayName}
      />
    )),
    (tags || []).map((tag) => <meta key={tag} property="og:article:tag" content={tag} />),
  ];
};

const getTwitterTags = (props: { card?: ITwitterCardFields }) => {
  if (!props.card || !props.card.fields) {
    return null;
  }
  const { cardType, site, creatorTwitterHandle, description, image, title } = props.card.fields;
  return [
    <meta key="twitter:card" name="twitter:card" content={cardType} />,
    <meta key="twitter:site" name="twitter:site" content={site} />,
    <meta key="twitter:creator" name="twitter:creator" content={creatorTwitterHandle} />,
    <meta key="twitter:title" name="twitter:title" content={title} />,
    <meta key="twitter:description" name="twitter:description" content={description} />,
    <meta key="twitter:image" name="twitter:image" content={image} />,
  ];
};

const getCrawlerTags = (props: { index: boolean; follow: boolean }) => {
  const { follow, index } = props;
  if (follow && index) {
    return null;
  }
  return [
    <meta
      key="robots"
      name="robots"
      content={[!follow && 'nofollow', !index && 'noindex'].filter(Boolean).join(',')}
    />,
  ];
};

type schemaType = {
  '@context': 'https://schema.org/';
  '@type': 'Article';
  dateModified: string;
  datePublished: string;
  image: string[];
  headline?: string;
  author?: {
    '@type': 'Person';
    name: string;
    url?: string;
    sameas: string[];
  }[];
  publisher?: {
    '@type': 'Organization';
    name: string;
    url?: string;
    sameas: string[];
    logo?: {
      '@type': 'ImageObject';
      url: string;
    };
  };
};

// Export only for testing
export const getArticleSchemaData = (props: {
  title: string | undefined;
  authors: IAuthorFields[];
  image: string | undefined;
  publisher: IPublisherFields | undefined;
  createdAt: string;
  updatedAt: string;
}) => {
  const { title, authors, image, publisher, createdAt, updatedAt } = props;

  const schemaData: schemaType = {
    '@context': 'https://schema.org/',
    '@type': 'Article',
    dateModified: createdAt,
    datePublished: updatedAt,
    ...(title && { headline: title }),
    // TODO: use custom image for each article page
    image: [
      image,
      'https://images.opendoor.com/source/s3/imgdrop-production/1afd9b4404c54cd5bd4d3737eec0d70d.jpg?preset=square-2048',
    ].filter(Boolean) as string[],
  };

  schemaData.author = (authors || []).map((author) => {
    const sameas = [];
    author.twitterUsername && sameas.push(getSocialLink('twitter', author.twitterUsername));
    author.linkedInUsername && sameas.push(getSocialLink('linkedin', author.linkedInUsername));
    author.instagramUsername && sameas.push(getSocialLink('instagram', author.instagramUsername));
    const url = author.website || sameas?.[0];
    return {
      '@type': 'Person',
      name: author.displayName,
      ...(url && { url }),
      ...(sameas && { sameas }),
    };
  });

  if (!publisher) {
    return schemaData;
  }
  const sameas = [];
  publisher.twitterUsername && sameas.push(getSocialLink('twitter', publisher.twitterUsername));
  publisher.linkedInUsername &&
    sameas.push(`https://www.linkedin.com/company/${publisher.linkedInUsername}`);
  publisher.instagramUsername &&
    sameas.push(getSocialLink('instagram', publisher.instagramUsername));
  publisher.youtubeChannel && sameas.push(`https://www.youtube.com/c/${publisher.youtubeChannel}`);
  publisher.facebookUsername && sameas.push(getSocialLink('facebook', publisher.facebookUsername));
  const url = publisher.url || sameas?.[0];

  schemaData.publisher = {
    '@type': 'Organization',
    name: publisher.name,
    ...(url && { url }),
    ...(sameas && { sameas }),
    ...(publisher.logoUrl && {
      logo: {
        '@type': 'ImageObject',
        url: publisher.logoUrl,
      },
    }),
  };
  return schemaData;
};

const SeoHead = (props: {
  base: IArticleFields['seoBase'];
  authors: IArticleFields['authors'];
  og: IArticleFields['openGraph'] | undefined;
  ogArticle: IArticleFields['openGraphArticle'];
  twitterCard: IArticleFields['twitterCard'];
  sys: { createdAt: string; updatedAt: string };
  publisher?: IArticleFields['publisher'];
  publishDate?: IArticleFields['publishDate'];
}) => {
  const { base, og, ogArticle, twitterCard, authors, sys, publisher, publishDate } = props;
  // ensure that you don't nest tags inside of fragments
  // if you do that, then the next head component
  // will have issues with overrides
  return (
    <Head>
      {getBaseTags({ base })}
      {getOgTags({
        og,
        ogArticle,
        createdAt: sys.createdAt,
        updatedAt: sys.updatedAt,
        authors,
      })}
      {getTwitterTags({ card: twitterCard })}
      {getCrawlerTags({ index: base?.fields.index ?? true, follow: base?.fields.follow ?? true })}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getArticleSchemaData({
              title: base?.fields.title,
              authors: (authors || []).map((author) => author.fields),
              image: og?.fields.image,
              publisher: publisher?.fields,
              createdAt: publishDate || sys.createdAt,
              updatedAt: sys.updatedAt,
            }),
          ),
        }}
      />
    </Head>
  );
};

export default SeoHead;
